import { SplitFactory } from '@splitsoftware/splitio';
import _ from 'lodash';

import {
  SPLIT_IO_API_KEY,
  USER_ANALYTICS_ATTRIBUTES,
} from '../account/appConstants';

/* The timeout changes were neccessary in order to accommodate
 * slower connections. This will make sure no errors get triggered
 * unnecessarily. By moving to a insanely long timeout period we hope
 * to remove all errors triggered by slow network speeds on the user side.
 * This essentially forces the SDK to make the initial requests only.
 * If a user stays on a page without refreshing for `Math.pow(10, 10)`
 * then we would make a few requests.
 */

const A_REALLY_LONG_TIME = Math.pow(10, 10);

/* Time to wait for a request before the SDK is ready. */
const SPLIT_READY_TIMEOUT = A_REALLY_LONG_TIME;

/* How many quick retries we will do while starting up the SDK */
const RETRIES_ON_FAILURE_BEFORE_READY = 1;

/* Maximum amount of time to wait before notifying a timeout */
const REQUEST_TIMEOUT_BEFORE_READY = A_REALLY_LONG_TIME;

/* The SDK polls Split servers for changes to feature roll-out plans.
 * This parameter controls this polling period in seconds. */
const FEATURES_REFRESH_RATE = A_REALLY_LONG_TIME;

/* The SDK polls Split servers for changes to segment definitions.
 * This parameter controls this polling period in seconds. */
const SEGMENTS_REFRESH_RATE = A_REALLY_LONG_TIME;

/* The SDK sends diagnostic metrics to Split servers.
 * This parameters controls this metric flush period in seconds. */
const METRICS_REFRESH_RATE = A_REALLY_LONG_TIME;

/* The SDK sends information on who got what treatment at what time
 * back to Split servers to power analytics. This parameter controls
 * how often this data is sent to Split servers. The parameter should
 * be in seconds. */
const IMPRESSION_REFRESH_RATE = A_REALLY_LONG_TIME;

/* The SDK will sends tracked events to Split servers.
 * This setting controls that flushing rate in seconds. */
const EVENTS_PUSH_RATE = A_REALLY_LONG_TIME;

/* Here you can set a specific timer for the
 * first push of events, starting on SDK initialization. */
const EVENTS_FIRST_PUSH_WINDOW = A_REALLY_LONG_TIME;

/* Storage type to be used by the SDK. */
const MEMORY_TYPE = 'LOCALSTORAGE';

class SplitClient {
  constructor() {
    if (SplitFactory) {
      this.factory = SplitFactory(this.config);
      this.client = this.factory.client();
      this.manager = this.factory.manager();
    }
  }

  get key() {
    const keys = _.get(USER_ANALYTICS_ATTRIBUTES, 'key');
    return keys.matching_key || keys.bucketing_key || keys;
  }

  get config() {
    return {
      core: {
        authorizationKey: SPLIT_IO_API_KEY,
        key: this.key,
      },
      startup: {
        readyTimeout: SPLIT_READY_TIMEOUT,
        retriesOnFailureBeforeReady: RETRIES_ON_FAILURE_BEFORE_READY,
        requestTimeoutBeforeReady: REQUEST_TIMEOUT_BEFORE_READY,
      },
      scheduler: {
        featuresRefreshRate: FEATURES_REFRESH_RATE,
        segmentsRefreshRate: SEGMENTS_REFRESH_RATE,
        metricsRefreshRate: METRICS_REFRESH_RATE,
        impressionsRefreshRate: IMPRESSION_REFRESH_RATE,
        eventsPushRate: EVENTS_PUSH_RATE,
        eventsFirstPushWindow: EVENTS_FIRST_PUSH_WINDOW,
      },
      storage: {
        type: MEMORY_TYPE,
      },
    };
  }

  allSplitTreatments(customAttributes) {
    return new Promise((resolve, reject) => {
      this.client.on(this.client.SDK_READY, () => {
        const names = this.manager.names();
        const mergedAttributes = Object.assign(
          USER_ANALYTICS_ATTRIBUTES,
          customAttributes
        );
        const treatments = this.client.getTreatments(names, mergedAttributes);
        resolve(treatments);
      });

      this.client.on(this.client.SDK_READY_TIMED_OUT, () => {
        reject({ error: 'Split SDK Timed out' });
      });
    });
  }
}

export default SplitClient;



// WEBPACK FOOTER //
// ./app/javascript/apps/application/split-client.js